/**
 * Competition Prep Mode — peaking protocol for powerlifters and strength athletes.
 *
 * Features:
 * - Meet day countdown
 * - Weight class tracking (current BW vs target class)
 * - Attempt selection helper (opener, 2nd, 3rd)
 * - Peaking protocol templates (linear, wave, DUP)
 * - Meet day checklist
 *
 * Revenue: Pro feature ($14.99/mo) or standalone $14.99/mo tier.
 */

import { supabase } from './supabase';
import { calculateE1RM } from './lifts/canonical';
import { createLogger } from './logger';

const log = createLogger('COMP_PREP');

export interface MeetSetup {
  id: string;
  user_id: string;
  meet_name: string;
  meet_date: string;
  weight_class_kg: number;
  federation: string; // USAPL, USPA, IPF, etc.
  lifts: {
    squat: { opener: number; second: number; third: number };
    bench: { opener: number; second: number; third: number };
    deadlift: { opener: number; second: number; third: number };
  };
  status: 'prep' | 'meet_day' | 'completed';
}

export interface AttemptRecommendation {
  lift: string;
  currentE1RM: number;
  opener: number; // ~88-90% of e1RM
  second: number; // ~93-95% of e1RM
  third: number; // ~97-102% of e1RM (PR attempt)
}

/**
 * Generate attempt recommendations based on current best lifts.
 */
export function recommendAttempts(
  currentBench1RM: number,
  currentSquat1RM: number,
  currentDeadlift1RM: number,
): AttemptRecommendation[] {
  const recommend = (lift: string, e1rm: number): AttemptRecommendation => ({
    lift,
    currentE1RM: Math.round(e1rm),
    opener: Math.round(e1rm * 0.89), // ~89% — confident, easy single
    second: Math.round(e1rm * 0.94), // ~94% — solid but challenging
    third: Math.round(e1rm * 1.00), // 100% — match PR or slight PR
  });

  return [
    recommend('Squat', currentSquat1RM),
    recommend('Bench Press', currentBench1RM),
    recommend('Deadlift', currentDeadlift1RM),
  ];
}

/**
 * Calculate projected total from attempt selections.
 */
export function projectedTotal(
  squatThird: number,
  benchThird: number,
  deadliftThird: number,
): { total: number; totalKg: number } {
  const totalLbs = squatThird + benchThird + deadliftThird;
  return {
    total: totalLbs,
    totalKg: Math.round(totalLbs * 0.45359237),
  };
}

/**
 * Days until meet.
 */
export function daysUntilMeet(meetDate: string): number {
  const diff = new Date(meetDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

/**
 * Weight cut status — current BW vs target weight class.
 */
export function weightCutStatus(
  currentBwLbs: number,
  targetClassKg: number,
): { currentKg: number; targetKg: number; deltaKg: number; onTrack: boolean } {
  const currentKg = Math.round(currentBwLbs * 0.45359237 * 10) / 10;
  const deltaKg = Math.round((currentKg - targetClassKg) * 10) / 10;
  return {
    currentKg,
    targetKg: targetClassKg,
    deltaKg,
    onTrack: deltaKg <= 2, // within 2kg of class = on track
  };
}

/**
 * Meet day checklist.
 */
export const MEET_DAY_CHECKLIST = [
  { id: 'singlet', label: 'Singlet', category: 'gear' },
  { id: 'belt', label: 'Belt (approved)', category: 'gear' },
  { id: 'shoes_squat', label: 'Squat shoes', category: 'gear' },
  { id: 'shoes_dead', label: 'Deadlift shoes/slippers', category: 'gear' },
  { id: 'wrist_wraps', label: 'Wrist wraps', category: 'gear' },
  { id: 'knee_sleeves', label: 'Knee sleeves', category: 'gear' },
  { id: 'chalk', label: 'Chalk', category: 'gear' },
  { id: 'federation_card', label: 'Federation membership card', category: 'docs' },
  { id: 'id', label: 'Photo ID', category: 'docs' },
  { id: 'food', label: 'Meet day food + water', category: 'nutrition' },
  { id: 'caffeine', label: 'Caffeine/pre-workout', category: 'nutrition' },
  { id: 'carbs', label: 'Fast carbs between lifts', category: 'nutrition' },
  { id: 'attempts', label: 'Attempt card filled out', category: 'prep' },
  { id: 'warmup_plan', label: 'Warm-up progression planned', category: 'prep' },
  { id: 'handler', label: 'Handler confirmed', category: 'prep' },
];

/**
 * Save meet setup to Supabase.
 */
export async function saveMeetSetup(setup: Omit<MeetSetup, 'id'>): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('meet_setups')
      .upsert({
        user_id: setup.user_id,
        meet_name: setup.meet_name,
        meet_date: setup.meet_date,
        weight_class_kg: setup.weight_class_kg,
        federation: setup.federation,
        lifts: setup.lifts,
        status: setup.status,
      })
      .select('id')
      .single();

    if (error) {
      log.error('saveMeetSetup failed:', error.message);
      return null;
    }
    return data?.id ?? null;
  } catch {
    return null;
  }
}
